import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CardContent } from '../ui/card';
import { FormSelect, FormSelectOption } from '../ui/form-select';
import { toast } from 'react-hot-toast';
import { cn } from '../../lib/utils';
import FlexibleTypeAhead from '../ui/flexible-typeahead';

// Import existing components
import TagsInput from '../ui/tags-input';
import VariantInput from '../ui/variant-input';
import ImageUrlsInput from '../ui/image-urls-input';
import DisplayNameInput from '../ui/display-name-input';

// Import hooks
import {
  useProductCategories,
  usePackForms,
  useConsumeTypes,
  useUnitTypes,
  useTherapeuticUsages,
  useCachedTypeaheadSearch,
} from '../../hooks/useUpmMasters';
import api from '../../lib/api';
import { STORAGE_KEY } from '../../lib/constants';

// Static options
const scheduleOptions = [
  { id: 0, value: 'N/A', label: 'N/A' },
  { id: 1, value: 'G', label: 'Schedule G' },
  { id: 2, value: 'H', label: 'Schedule H' },
  { id: 3, value: 'H1', label: 'Schedule H1' },
  { id: 4, value: 'NS', label: 'Schedule NS' },
  { id: 5, value: 'OTC', label: 'Schedule OTC' },
  { id: 6, value: 'X', label: 'Schedule X' },
];

const coldStorageOptions = [
  { id: 0, value: 'N/A', label: 'N/A' },
  { id: 1, value: 'Room Temperature', label: 'Room Temperature' },
  { id: 2, value: 'Refrigerated (2-8°C)', label: 'Refrigerated (2-8°C)' },
  { id: 3, value: 'Frozen (-20°C)', label: 'Frozen (-20°C)' },
  { id: 4, value: 'Deep Freeze (-80°C)', label: 'Deep Freeze (-80°C)' },
  { id: 5, value: 'Controlled Room Temperature', label: 'Controlled Room Temperature' },
];

const chronicTypeOptions = [
  { id: 0, value: 'N/A', label: 'N/A' },
  { id: 1, value: 'Chronic', label: 'Chronic' },
  { id: 2, value: 'Non-Chronic', label: 'Non Chronic' },
  { id: 3, value: 'Probably-Chronic', label: 'Probably Chronic' },
];

// Zod schema with strict validation
const baseFormSchema = z.object({
  sku_sub_category: z.string().min(1, 'Category is required'),
  sku_sub_category_id: z.number().min(0, 'Category is required'),
  sku_name: z.string().min(1, 'SKU Name is required'),
  mrp_price: z.number().min(0, 'MRP Price must be positive'),
  manufacturer_name: z.string().min(1, 'Manufacturer is required'),
  manufacturer_id: z.number().min(0, 'Manufacturer is required'),
  brand: z.string().min(1, 'Brand is required'),
  brand_id: z.number().min(0, 'Brand is required'),
  pack_form: z.string().min(1, 'Pack Form is required'),
  pack_form_id: z.number().min(0, 'Pack Form is required'),
  drug_form: z.string().min(1, 'Drug Form is required'),
  drug_form_id: z.number().min(0, 'Drug Form is required'),
  schedule: z.string().min(1, 'Schedule is required'),
  hsn_name: z.string().min(1, 'HSN is required'),
  hsn_id: z.number().min(0, 'HSN is required'),
  cold_storage: z.string().min(1, 'Cold Storage is required'),
  cold_storage_id: z.number().min(0, 'Cold Storage is required'),
  consume_type: z.string().min(1, 'Consume Type is required'),
  consume_type_id: z.number().min(0, 'Consume Type is required'),
  item_size: z.string().min(1, 'Item Size is required'),
  unit_type: z.string().min(1, 'Unit Type is required'),
  unit_type_id: z.number().min(0, 'Unit Type is required'),
  units_in_pack: z.number().min(1, 'Units in Pack must be at least 1'),
  chronic_type: z.string().min(1, 'Chronic Type is required'),
  chronic_type_id: z.number().min(0, 'Chronic Type is required'),
  therapeutic_usage: z.string().min(1, 'Therapeutic Usage is required'),
  therapeutic_usage_id: z.number().min(0, 'Therapeutic Usage is required'),
  composition: z.string().optional(),
  composition_id: z.number().optional(),
  product_url: z.string().url().optional().or(z.literal('')),
  image_urls: z.array(z.string()).optional(),
  tags: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
  variants: z.array(z.object({
    category: z.object({ id: z.union([z.string(), z.number()]).transform(val => String(val)), name: z.string() }),
    value: z.object({
      id: z.union([z.string(), z.number()]).transform(val => String(val)),
      name: z.string(),
      variant_category_id: z.union([z.string(), z.number()]).transform(val => String(val))
    })
  })).optional(),
  display_name: z.string().min(1, 'Display Name is required'),
  is_draft: z.boolean().optional(),
});

type FormData = z.infer<typeof baseFormSchema>;

export interface UpmProductFormData {
  sku_name: string;
  flavour?: string;
  mrp_price: number;
  manufacturer_name: string;
  manufacturer_id?: number;
  sku_sub_category: string;
  sku_sub_category_id?: number;
  pack_form: string;
  pack_form_id?: number;
  drug_form: string;
  drug_form_id?: number;
  schedule: string;
  schedule_id: number;
  brand: string;
  brand_id?: number;
  hsn_name: string;
  hsn_id?: number;
  cold_storage: string;
  cold_storage_id?: number;
  consume_type: string;
  consume_type_id?: number;
  item_size: string;
  units_in_pack: number;
  unit_type: string;
  unit_type_id?: number;
  chronic_type: string;
  chronic_type_id?: number;
  therapeutic_usage: string;
  therapeutic_usage_id?: number;
  composition?: string;
  composition_id?: number;
  tags?: Array<{ id: string; name: string }>;
  variants?: Array<{ category: { id: string; name: string }; value: { id: string; name: string; variant_category_id: string } }>;
  image_urls: string[];
  product_url?: string;
  display_name: string;
  is_draft?: boolean;
}

interface UpmProductsFormProps {
  initialData?: Partial<UpmProductFormData>;
  onSubmit: (data: UpmProductFormData) => void | Promise<void>;
  onCancel?: () => void;
  submitText?: string;
  cancelText?: string;
  loading?: boolean;
  className?: string;
}

const NALIST = ['NA', 'N/A', 'na', 'n/a'];

const UpmProductsForm: React.FC<UpmProductsFormProps> = ({
  initialData = {},
  onSubmit,
  onCancel,
  submitText = 'Create Product',
  cancelText = 'Cancel',
  loading = false,
  className,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisplayNameManuallyEdited, setIsDisplayNameManuallyEdited] = useState(false);
  const queryClient = useQueryClient();

  // Fetch data
  const productCategories = useProductCategories();
  const packForms = usePackForms();
  const consumeTypes = useConsumeTypes();
  const unitTypes = useUnitTypes();
  const therapeuticUsages = useTherapeuticUsages();

  const formSchema = useMemo(() => baseFormSchema.superRefine((data, ctx) => {
    const requiresComposition = data.sku_sub_category && (data.sku_sub_category ?? '').toLowerCase().includes('rx');
    if (!requiresComposition) {
      return;
    }

    const compositionValue = data.composition ?? '';
    const trimmedComposition = compositionValue.trim();

    if (!trimmedComposition || NALIST.includes(compositionValue) || NALIST.includes(trimmedComposition)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Composition is required for the selected category and cannot be N/A',
        path: ['composition'],
      });
    }
  }), []);

  const allowedEmails = process.env.REACT_APP_ALLOWED_EMAILS?.split(',') || [];
  const isAllowedEmail = () => {
    const user = localStorage.getItem(STORAGE_KEY);
    const userData = user ? JSON.parse(user) : null;
    if(userData && userData.email) {
      return allowedEmails.includes(userData.email);
    }
    return false;
  }
  // Efficient search functions using pre-fetched data
  const usePrefetchSearch = (data: any[], query: string, minLength: number = 0) => {
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
      if (query.length >= minLength && data) {
        setIsLoading(true);
        const filtered = data
          .filter(item =>
            item.name?.toLowerCase().includes(query.toLowerCase()) ||
            item.label?.toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, 10); // Limit to 10 results for performance
        setSearchResults(filtered);
        setIsLoading(false);
      } else {
        setSearchResults([]);
      }
    }, [query, data, minLength]);

    return {
      data: searchResults,
      isLoading,
      error: null
    };
  };

  // Create search functions for each master data type
  const usePackFormSearch = (query: string) => usePrefetchSearch(packForms.data || [], query);
  const useConsumeTypeSearch = (query: string) => usePrefetchSearch(consumeTypes.data || [], query);
  const useUnitTypeSearch = (query: string) => usePrefetchSearch(unitTypes.data || [], query);
  const useTherapeuticUsageSearch = (query: string) => usePrefetchSearch(therapeuticUsages.data || [], query);

  // Keep API-based search for fields that need real-time search (large datasets)
  const useManufacturerSearch = (query: string) => {
    return useCachedTypeaheadSearch('manufacturer', query, query.length >= 2);
  };

  const useBrandSearch = (query: string) => {
    return useCachedTypeaheadSearch('brand', query, query.length >= 2);
  };

  const useDrugFormSearch = (query: string) => {
    return useCachedTypeaheadSearch('drug_form', query, query.length >= 2);
  };

  const useHSNSearch = (query: string) => {
    return useCachedTypeaheadSearch('hsn', query, query.length >= 2);
  };

  const useCompositionSearch = (query: string) => {
    return useCachedTypeaheadSearch('composition', query, query.length >= 4);
  };

  // API functions for adding new values with cache invalidation
  const addManufacturer = async (name: string) => {
    const response = await api.createUpmMasters('manufacturer', name);
    // Invalidate and refetch manufacturer data
    await queryClient.invalidateQueries({ queryKey: ['upm-masters', 'manufacturer'] });
    // Extract the first item from the response data array and format it as an option
    const newItem = response.data?.[0];
    return newItem ? { id: newItem.id, name: newItem.name } : null;
  };

  const addBrand = async (name: string) => {
    const response = await api.createUpmMasters('brand', name);
    // Invalidate and refetch brand data
    await queryClient.invalidateQueries({ queryKey: ['upm-masters', 'brand'] });
    // Extract the first item from the response data array and format it as an option
    const newItem = response.data?.[0];
    return newItem ? { id: newItem.id, name: newItem.name } : null;
  };

  const addDrugForm = async (name: string) => {
    const response = await api.createUpmMasters('drug_form', name);
    // Invalidate and refetch drug form data
    await queryClient.invalidateQueries({ queryKey: ['upm-masters', 'drug_form'] });
    // Extract the first item from the response data array and format it as an option
    const newItem = response.data?.[0];
    return newItem ? { id: newItem.id, name: newItem.name } : null;
  };

  const addHSN = async (name: string) => {
    const response = await api.createUpmMasters('hsn', name);
    // Invalidate and refetch HSN data
    await queryClient.invalidateQueries({ queryKey: ['upm-masters', 'hsn'] });
    // Extract the first item from the response data array and format it as an option
    const newItem = response.data?.[0];
    return newItem ? { id: newItem.id, name: newItem.name } : null;
  };

  const addComposition = async (name: string) => {
    const response = await api.createUpmMasters('composition', name);
    // Invalidate and refetch composition data
    await queryClient.invalidateQueries({ queryKey: ['upm-masters', 'composition'] });
    // Extract the first item from the response data array and format it as an option
    const newItem = response.data?.[0];
    return newItem ? { id: newItem.id, name: newItem.name } : null;
  };

  const addPackForm = async (name: string) => {
    const response = await api.createUpmMasters('pack_form', name);
    // Invalidate and refetch pack form data
    await queryClient.invalidateQueries({ queryKey: ['upm-masters', 'pack_form'] });
    // Extract the first item from the response data array and format it as an option
    const newItem = response.data?.[0];
    return newItem ? { id: newItem.id, name: newItem.name } : null;
  };

  const addConsumeType = async (name: string) => {
    const response = await api.createUpmMasters('consume_type', name);
    // Invalidate and refetch consume type data
    await queryClient.invalidateQueries({ queryKey: ['upm-masters', 'consume_type'] });
    // Extract the first item from the response data array and format it as an option
    const newItem = response.data?.[0];
    return newItem ? { id: newItem.id, name: newItem.name } : null;
  };

  const addUnitType = async (name: string) => {
    const response = await api.createUpmMasters('unit_type', name);
    // Invalidate and refetch unit type data
    await queryClient.invalidateQueries({ queryKey: ['upm-masters', 'unit_type'] });
    // Extract the first item from the response data array and format it as an option
    const newItem = response.data?.[0];
    return newItem ? { id: newItem.id, name: newItem.name } : null;
  };

  const addTherapeuticUsage = async (name: string) => {
    const response = await api.createUpmMasters('therapeutic_usage', name);
    // Invalidate and refetch therapeutic usage data
    await queryClient.invalidateQueries({ queryKey: ['upm-masters', 'therapeutic_usage'] });
    // Extract the first item from the response data array and format it as an option
    const newItem = response.data?.[0];
    return newItem ? { id: newItem.id, name: newItem.name } : null;
  };

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      sku_sub_category: '',
      sku_sub_category_id: undefined,
      sku_name: '',
      mrp_price: 0,
      manufacturer_name: '',
      manufacturer_id: undefined,
      brand: '',
      brand_id: undefined,
      pack_form: '',
      pack_form_id: undefined,
      drug_form: '',
      drug_form_id: undefined,
      schedule: '',
      schedule_id: 0,
      hsn_name: '',
      hsn_id: undefined,
      cold_storage: '',
      cold_storage_id: undefined,
      consume_type: '',
      consume_type_id: undefined,
      item_size: '',
      unit_type: '',
      unit_type_id: undefined,
      units_in_pack: 1,
      chronic_type: '',
      chronic_type_id: undefined,
      therapeutic_usage: '',
      therapeutic_usage_id: undefined,
      composition: '',
      composition_id: undefined,
      product_url: '',
      image_urls: [],
      tags: [],
      variants: [],
      display_name: '',
      is_draft: false,
      ...initialData,
    },
  });

  const { watch, setValue, control, handleSubmit, formState: { errors } } = form;
  const watchedValues = watch();

  // Helper function to move to next field on Enter/Tab
  const handleFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement | Element>, fieldName: string) => {
    // Only handle Enter key, Tab is handled natively
    if (e.key === 'Enter' && !e.shiftKey) {
      const currentValue = watchedValues[fieldName as keyof typeof watchedValues];
      // Only move to next field if current field has a value
      if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
        e.preventDefault();
        // Get all form inputs in order
        const formInputs = Array.from(
          document.querySelectorAll<HTMLElement>(
            'input:not([type="submit"]):not([type="button"]), select, textarea'
          )
        ).filter(el => {
          // Filter out hidden inputs and buttons
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && !el.hasAttribute('readonly');
        });

        const currentIndex = formInputs.findIndex(el =>
          el === e.currentTarget || el.contains(e.currentTarget as Node)
        );

        if (currentIndex >= 0 && currentIndex < formInputs.length - 1) {
          const nextInput = formInputs[currentIndex + 1];
          nextInput?.focus();
          // For select elements, open the dropdown
          if (nextInput instanceof HTMLSelectElement) {
            nextInput.focus();
          }
        }
      }
    }
  };

  // Check if composition is required based on the selected category
  const requiresComposition = watchedValues.sku_sub_category &&
    watchedValues.sku_sub_category.toLowerCase().includes('rx');

  const hasValidComposition = watchedValues.composition &&
    watchedValues.composition !== 'N/A' &&
    watchedValues.composition !== 'NA' &&
    watchedValues.composition.trim() !== '';

  // Check if all mandatory fields are filled (except composition for RX/RX_OTC)
  const mandatoryFields = [
    'sku_name', 'mrp_price', 'manufacturer_name', 'brand', 'pack_form',
    'drug_form', 'schedule', 'hsn_name', 'cold_storage', 'consume_type',
    'item_size', 'unit_type', 'units_in_pack', 'chronic_type',
    'therapeutic_usage', 'display_name'
  ];

  const allMandatoryFieldsFilled = mandatoryFields.every(field => {
    const value = watchedValues[field as keyof typeof watchedValues];
    return value !== undefined && value !== null && value !== '' &&
            (typeof value !== 'number' || value > 0);
  });

  // User can only save as draft if:
  // 1. All mandatory fields are filled AND
  // 2. Composition is missing for RX/RX_OTC categories
  const canSaveAsDraft = allMandatoryFieldsFilled && requiresComposition && !hasValidComposition;

  // User can save as product if:
  // 1. All mandatory fields are filled AND
  // 2. If RX/RX_OTC, composition must be valid
  const canSaveAsProduct = allMandatoryFieldsFilled && (!requiresComposition || hasValidComposition);

  const isDraft = canSaveAsDraft;
  const dynamicSubmitText = isDraft ? 'Save as Draft' : submitText;

  // Generate display name
  const generateDisplayName = (data: FormData): string => {
    const parts: string[] = [];

    if (data.sku_name) {
      parts.push(data.sku_name);
    }

    if (data.brand) {
      parts.push(data.brand);
    }

    if (data.pack_form) {
      parts.push(data.pack_form);
    }

    if (data.item_size) {
      parts.push(data.item_size);
    }

    // Add variants if any
    if (data.variants && Array.isArray(data.variants) && data.variants.length > 0) {
      const variantValues = data.variants
        .filter((v) => v && v.category && v.value && v.value.name !== 'N/A')
        .map((v) => v.value.name);

      if (variantValues.length > 0) {
        parts.push(...variantValues);
      }
    }

    return parts.length > 0 ? parts.join(' | ') : '';
  };

  // Auto-update display name
  useEffect(() => {
    if (!isDisplayNameManuallyEdited) {
      const displayName = generateDisplayName(watchedValues);
      if (displayName && displayName !== watchedValues.display_name) {
        setValue('display_name', displayName);
      }
    }
  }, [watchedValues.sku_name, watchedValues.brand, watchedValues.pack_form, watchedValues.item_size, watchedValues.variants, isDisplayNameManuallyEdited, setValue, watchedValues.display_name]);

  const handleFormSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      // Check if user can save as draft or product
      if (!canSaveAsDraft && !canSaveAsProduct) {
        toast.error('Please fill all mandatory fields before saving');
        setIsSubmitting(false);
        return;
      }

      // Set is_draft flag based on current state
      const submitData = {
        ...data,
        is_draft: isDraft
      };

      await onSubmit(submitData as UpmProductFormData);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormError = (errors: any) => {
    console.error('Form validation errors:', errors);
    // Show first error to user
    const firstError = Object.values(errors)[0] as any;
    if (firstError?.message) {
      toast.error(firstError.message);
    } else {
      toast.error('Please fix form errors before submitting');
    }
  };

  return (
    <CardContent>
      <form onSubmit={handleSubmit(handleFormSubmit, handleFormError)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Category */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="sku_sub_category">Category *</Label>
            <Controller
              name="sku_sub_category"
              control={control}
              render={({ field }) => {
                const categoryOptions: FormSelectOption[] = [
                  { id: 0, value: 'N/A', label: 'N/A' },
                  ...(productCategories.data
                    ?.filter((cat: any) => cat.id !== 0)
                    .map((category: any) => ({
                      id: category.id,
                      value: category.name,
                      label: category.name,
                    })) || [])
                ];

                return (
                  <FormSelect
                    value={field.value}
                    onChange={(value) => {
                      if (value === 'N/A') {
                        field.onChange('N/A');
                        setValue('sku_sub_category_id', 0);
                      } else {
                        const option = productCategories.data?.find(
                          (cat: any) => cat.name === value,
                        );
                        field.onChange(option?.name || '');
                        setValue('sku_sub_category_id', Number(option?.id) || 0);
                      }
                    }}
                    onKeyDown={(e) => handleFieldKeyDown(e, 'sku_sub_category')}
                    options={categoryOptions}
                    placeholder="Select category"
                    className={errors.sku_sub_category ? 'border-red-500' : ''}
                  />
                );
              }}
            />
            {errors.sku_sub_category && (
              <p className="text-sm text-red-500">{errors.sku_sub_category.message}</p>
            )}
          </div>

          {/* SKU Name */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="sku_name">SKU Name *</Label>
            <Controller
              name="sku_name"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter SKU name"
                  onKeyDown={(e) => handleFieldKeyDown(e, 'sku_name')}
                  className={cn(
                    "bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]",
                    errors.sku_name ? 'border-red-500' : ''
                  )}
                />
              )}
            />
            {errors.sku_name && (
              <p className="text-sm text-red-500">{errors.sku_name.message}</p>
            )}
          </div>

          {/* MRP Price */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="mrp_price">MRP Price *</Label>
            <Controller
              name="mrp_price"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  onKeyDown={(e) => handleFieldKeyDown(e, 'mrp_price')}
                  className={cn(
                    "bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]",
                    errors.mrp_price ? 'border-red-500' : ''
                  )}
                />
              )}
            />
            {errors.mrp_price && (
              <p className="text-sm text-red-500">{errors.mrp_price.message}</p>
            )}
          </div>

          {/* Manufacturer */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="manufacturer_name">Manufacturer *</Label>
            <Controller
              name="manufacturer_name"
              control={control}
              render={({ field }) => (
                <FlexibleTypeAhead
                  value={field.value}
                  onChange={field.onChange}
                  onSelect={(option) => {
                    field.onChange(option.name);
                    setValue('manufacturer_id', Number(option.id) || 0);
                    setValue('manufacturer_name', option.name);
                  }}
                  searchHook={useManufacturerSearch}
                  onAdd={addManufacturer}
                  placeholder="Search manufacturer"
                  allowAdd
                  addText="Add manufacturer"
                  debounceMs={500}
                  className={cn(
                    "bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]",
                    errors.manufacturer_name ? 'border-red-500' : ''
                  )}
                />
              )}
            />
            {errors.manufacturer_name && (
              <p className="text-sm text-red-500">{errors.manufacturer_name.message}</p>
            )}
          </div>

          {/* Brand */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="brand">Brand *</Label>
            <Controller
              name="brand"
              control={control}
              render={({ field }) => (
                <FlexibleTypeAhead
                  value={field.value}
                  onChange={field.onChange}
                  onSelect={(option) => {
                    field.onChange(option.name);
                    setValue('brand_id', Number(option.id) || 0);
                    setValue('brand', option.name);
                  }}
                  searchHook={useBrandSearch}
                  onAdd={addBrand}
                  placeholder="Search brand"
                  allowAdd
                  addText="Add brand"
                  debounceMs={500}
                  className={cn(
                    "bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]",
                    errors.brand ? 'border-red-500' : ''
                  )}
                />
              )}
            />
            {errors.brand && (
              <p className="text-sm text-red-500">{errors.brand.message}</p>
            )}
          </div>

          {/* Pack Form */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="pack_form">Pack Form *</Label>
            <Controller
              name="pack_form"
              control={control}
              render={({ field }) => (
                <FlexibleTypeAhead
                  value={field.value}
                  onChange={field.onChange}
                  onSelect={(option) => {
                    field.onChange(option.name);
                    setValue('pack_form_id', Number(option.id) || 0);
                    setValue('pack_form', option.name);
                  }}
                  searchHook={usePackFormSearch}
                  onAdd={addPackForm}
                  placeholder="Search pack form"
                  allowAdd
                  addText="Add pack form"
                  debounceMs={500}
                  className={cn(
                    "bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]",
                    errors.pack_form ? 'border-red-500' : ''
                  )}
                />
              )}
            />
            {errors.pack_form && (
              <p className="text-sm text-red-500">{errors.pack_form.message}</p>
            )}
          </div>

          {/* Drug Form */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="drug_form">Drug Form *</Label>
            <Controller
              name="drug_form"
              control={control}
              render={({ field }) => (
                <FlexibleTypeAhead
                  value={field.value}
                  onChange={field.onChange}
                  onSelect={(option) => {
                    field.onChange(option.name);
                    setValue('drug_form_id', Number(option.id) || 0);
                    setValue('drug_form', option.name);
                  }}
                  searchHook={useDrugFormSearch}
                  onAdd={addDrugForm}
                  placeholder="Search drug form"
                  allowAdd
                  addText="Add drug form"
                  debounceMs={500}
                  className={cn(
                    "bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]",
                    errors.drug_form ? 'border-red-500' : ''
                  )}
                />
              )}
            />
            {errors.drug_form && (
              <p className="text-sm text-red-500">{errors.drug_form.message}</p>
            )}
          </div>

          {/* Schedule */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="schedule">Schedule *</Label>
            <Controller
              name="schedule"
              control={control}
              render={({ field }) => {
                const scheduleOptionsForm: FormSelectOption[] = scheduleOptions.map((option) => ({
                  id: option.id,
                  value: option.value,
                  label: option.label
                }));

                return (
                  <FormSelect
                    value={field.value}
                    onChange={(value) => {
                      const option = scheduleOptions.find(opt => opt.value === value);
                      field.onChange(option?.value || '');
                      setValue('schedule', option?.value || '');
                    }}
                    onKeyDown={(e) => handleFieldKeyDown(e, 'schedule')}
                    options={scheduleOptionsForm}
                    placeholder="Select schedule"
                    className={errors.schedule ? 'border-red-500' : ''}
                  />
                );
              }}
            />
            {errors.schedule && (
              <p className="text-sm text-red-500">{errors.schedule.message}</p>
            )}
          </div>

          {/* HSN Name */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="hsn_name">HSN Name *</Label>
            <Controller
              name="hsn_name"
              control={control}
              render={({ field }) => (
                <FlexibleTypeAhead
                  value={field.value}
                  onChange={field.onChange}
                  onSelect={(option) => {
                    field.onChange(option.name);
                    setValue('hsn_id', Number(option.id) || 0);
                    setValue('hsn_name', option.name);
                  }}
                  searchHook={useHSNSearch}
                  onAdd={addHSN}
                  placeholder="Search HSN code or enter N/A"
                  allowAdd
                  addText="Add HSN"
                  debounceMs={500}
                  className={cn(
                    "bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]",
                    errors.hsn_name ? 'border-red-500' : ''
                  )}
                />
              )}
            />
            {errors.hsn_name && (
              <p className="text-sm text-red-500">{errors.hsn_name.message}</p>
            )}
          </div>

          {/* Cold Storage */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="cold_storage">Cold Storage *</Label>
            <Controller
              name="cold_storage"
              control={control}
              render={({ field }) => {
                const coldStorageOptionsForm: FormSelectOption[] = coldStorageOptions.map((option) => ({
                  id: option.id,
                  value: option.label,
                  label: option.label
                }));

                return (
                  <FormSelect
                    value={field.value}
                    onChange={(value) => {
                      const option = coldStorageOptions.find(opt => opt.label === value);
                      field.onChange(option?.label || '');
                      setValue('cold_storage_id', option?.id || 0);
                    }}
                    onKeyDown={(e) => handleFieldKeyDown(e, 'cold_storage')}
                    options={coldStorageOptionsForm}
                    placeholder="Select cold storage"
                    className={errors.cold_storage ? 'border-red-500' : ''}
                  />
                );
              }}
            />
            {errors.cold_storage && (
              <p className="text-sm text-red-500">{errors.cold_storage.message}</p>
            )}
          </div>

          {/* Consume Type */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="consume_type">Consume Type *</Label>
            <Controller
              name="consume_type"
              control={control}
              render={({ field }) => (
                <FlexibleTypeAhead
                  value={field.value}
                  onChange={field.onChange}
                  onSelect={(option) => {
                    field.onChange(option.name);
                    setValue('consume_type_id', Number(option.id) || 0);
                    setValue('consume_type', option.name);
                  }}
                  searchHook={useConsumeTypeSearch}
                  onAdd={addConsumeType}
                  placeholder="Search consume type"
                  allowAdd
                  addText="Add consume type"
                  debounceMs={500}
                  className={cn(
                    "bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]",
                    errors.consume_type ? 'border-red-500' : ''
                  )}
                />
              )}
            />
            {errors.consume_type && (
              <p className="text-sm text-red-500">{errors.consume_type.message}</p>
            )}
          </div>

          {/* Item Size */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="item_size">Item Size *</Label>
            <Controller
              name="item_size"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter item size"
                  onKeyDown={(e) => handleFieldKeyDown(e, 'item_size')}
                  className={cn(
                    "bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]",
                    errors.item_size ? 'border-red-500' : ''
                  )}
                />
              )}
            />
            {errors.item_size && (
              <p className="text-sm text-red-500">{errors.item_size.message}</p>
            )}
          </div>

          {/* Unit Type */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="unit_type">Unit Type *</Label>
            <Controller
              name="unit_type"
              control={control}
              render={({ field }) => (
                <FlexibleTypeAhead
                  value={field.value}
                  onChange={field.onChange}
                  onSelect={(option) => {
                    field.onChange(option.name);
                    setValue('unit_type_id', Number(option.id) || 0);
                    setValue('unit_type', option.name);
                  }}
                  searchHook={useUnitTypeSearch}
                  onAdd={addUnitType}
                  placeholder="Search unit type"
                  allowAdd
                  addText="Add unit type"
                  debounceMs={500}
                  className={cn(
                    "bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]",
                    errors.unit_type ? 'border-red-500' : ''
                  )}
                />
              )}
            />
            {errors.unit_type && (
              <p className="text-sm text-red-500">{errors.unit_type.message}</p>
            )}
          </div>

          {/* Units in Pack */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="units_in_pack">Units in Pack *</Label>
            <Controller
              name="units_in_pack"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  placeholder="Enter units in pack"
                  onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                  onKeyDown={(e) => handleFieldKeyDown(e, 'units_in_pack')}
                  className={cn(
                    "bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]",
                    errors.units_in_pack ? 'border-red-500' : ''
                  )}
                />
              )}
            />
            {errors.units_in_pack && (
              <p className="text-sm text-red-500">{errors.units_in_pack.message}</p>
            )}
          </div>

          {/* Chronic Type */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="chronic_type">Chronic Type *</Label>
            <Controller
              name="chronic_type"
              control={control}
              render={({ field }) => {
                const chronicTypeOptionsForm: FormSelectOption[] = chronicTypeOptions.map((option) => ({
                  id: option.id,
                  value: option.label,
                  label: option.label
                }));

                return (
                  <FormSelect
                    value={field.value}
                    onChange={(value) => {
                      const option = chronicTypeOptions.find(opt => opt.label === value);
                      field.onChange(option?.label || '');
                      setValue('chronic_type_id', option?.id || 0);
                    }}
                    onKeyDown={(e) => handleFieldKeyDown(e, 'chronic_type')}
                    options={chronicTypeOptionsForm}
                    placeholder="Select chronic type"
                    className={errors.chronic_type ? 'border-red-500' : ''}
                  />
                );
              }}
            />
            {errors.chronic_type && (
              <p className="text-sm text-red-500">{errors.chronic_type.message}</p>
            )}
          </div>

          {/* Therapeutic Usage */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="therapeutic_usage">Therapeutic Usage *</Label>
            <Controller
              name="therapeutic_usage"
              control={control}
              render={({ field }) => (
                <FlexibleTypeAhead
                  value={field.value}
                  onChange={field.onChange}
                  onSelect={(option) => {
                    field.onChange(option.name);
                    setValue('therapeutic_usage_id', Number(option.id) || 0);
                    setValue('therapeutic_usage', option.name);
                  }}
                  searchHook={useTherapeuticUsageSearch}
                  onAdd={addTherapeuticUsage}
                  placeholder="Search therapeutic usage"
                  allowAdd
                  addText="Add therapeutic usage"
                  debounceMs={500}
                  className={cn(
                    "bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]",
                    errors.therapeutic_usage ? 'border-red-500' : ''
                  )}
                />
              )}
            />
            {errors.therapeutic_usage && (
              <p className="text-sm text-red-500">{errors.therapeutic_usage.message}</p>
            )}
          </div>

          {/* Composition */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="composition">
              Salt Name + Strength (Molecular Composition)
              {requiresComposition && ' *'}
            </Label>
            <Controller
              name="composition"
              control={control}
              render={({ field }) => (
                <FlexibleTypeAhead
                  value={field.value || ''}
                  onChange={field.onChange}
                  onSelect={(option) => {
                    // Prevent N/A selection for RX/RX_OTC categories
                    if (requiresComposition && (option.name === 'N/A' || option.name === 'NA')) {
                      toast.error('N/A is not allowed for RX and RX_OTC categories');
                      return;
                    }
                    field.onChange(option.name);
                    setValue('composition_id', Number(option.id) || 0);
                    setValue('composition', option.name);
                  }}
                  searchHook={useCompositionSearch}
                  onAdd={addComposition}
                  placeholder={requiresComposition ? "Search composition (required)" : "Search composition"}
                  allowAdd={isAllowedEmail() ? true : false}
                  addText="Add composition"
                  minSearchLength={4}
                  debounceMs={500}
                  className={cn(
                    "bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]",
                    errors.composition ? 'border-red-500' : '',
                    requiresComposition ? 'border-red-300' : ''
                  )}
                />
              )}
            />
            {errors.composition && (
              <p className="text-sm text-red-500">{errors.composition.message}</p>
            )}
            {requiresComposition && (
              <p className="text-sm text-amber-600">Required for RX and RX_OTC categories</p>
            )}
          </div>

          {/* Product URL */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="product_url">Product URL</Label>
            <Controller
              name="product_url"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="url"
                  placeholder="Enter product URL"
                  onKeyDown={(e) => handleFieldKeyDown(e, 'product_url')}
                  className={cn(
                    "bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]",
                    errors.product_url ? 'border-red-500' : ''
                  )}
                />
              )}
            />
            {errors.product_url && (
              <p className="text-sm text-red-500">{errors.product_url.message}</p>
            )}
          </div>

          {/* Image URLs */}
          <div className="space-y-1 md:col-span-2">
            <Label className="text-gray-800" htmlFor="image_urls">Product Images *</Label>
            <Controller
              name="image_urls"
              control={control}
              render={({ field }) => (
                <ImageUrlsInput
                  value={field?.value ?? []}
                  onChange={field.onChange}
                  className={errors.image_urls ? 'border-red-500' : ''}
                />
              )}
            />
            {errors.image_urls && (
              <p className="text-sm text-red-500">{errors.image_urls.message}</p>
            )}
          </div>

          {/* Tags */}
          <div className="space-y-1 md:col-span-2">
            <Label className="text-gray-800" htmlFor="tags">Tags</Label>
            <Controller
              name="tags"
              control={control}
              render={({ field }) => (
                <TagsInput
                  value={field.value?.map(tag => ({ id: Number(tag.id), name: tag.name })) || []}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          {/* Variants */}
          <div className="space-y-1">
            <Label className="text-gray-800" htmlFor="variants">Variants</Label>
            <Controller
              name="variants"
              control={control}
              render={({ field }) => (
                <VariantInput
                  value={field.value || []}
                  onChange={field.onChange}
                />
              )}
            />
            {errors.variants && (
              <p className="text-sm text-red-500">{errors.variants.message}</p>
            )}
          </div>

          {/* Display Name */}
          <div className="space-y-1 md:col-span-3">
            <Label className="text-gray-800" htmlFor="display_name">Display Name *</Label>
            <Controller
              name="display_name"
              control={control}
              render={({ field }) => (
                <DisplayNameInput
                  value={field.value || ''}
                  onChange={(value) => {
                    field.onChange(value);
                    setIsDisplayNameManuallyEdited(true);
                  }}
                  className={errors.display_name ? 'border-red-500' : ''}
                />
              )}
            />
            {errors.display_name && (
              <p className="text-sm text-red-500">{errors.display_name.message}</p>
            )}
            <p className="text-sm text-gray-600">
              Auto-generated from SKU Name, Brand, Pack Form, Item Size, and Variants (editable)
            </p>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4 pt-6">
          {onCancel && (
            <Button type="button" variant="default" onClick={onCancel}>
              {cancelText}
            </Button>
          )}
          <Button
            variant="outline"
            type="submit"
            disabled={loading || isSubmitting || (!canSaveAsDraft && !canSaveAsProduct)}
          >
            {loading || isSubmitting ? 'Submitting...' : dynamicSubmitText}
          </Button>
        </div>

        {/* Status Messages */}
        {!allMandatoryFieldsFilled && (
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
            <strong>Missing Required Fields:</strong> Please fill all mandatory fields before saving.
          </div>
        )}

        {allMandatoryFieldsFilled && requiresComposition && !hasValidComposition && (
          <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-md">
            <strong>Draft Mode:</strong> Composition is required for RX/RX_OTC categories. You can save as draft for now.
          </div>
        )}

        {canSaveAsProduct && (
          <div className="text-sm text-green-600 bg-green-50 p-3 rounded-md">
            <strong>Ready to Submit:</strong> All required fields are filled. You can create the product.
          </div>
        )}
      </form>
    </CardContent>
  );
};

export default UpmProductsForm;
